import React from 'react';
import {
  TextField,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  FormHelperText,
  Grid,
  Typography,
  Box,
  Button,
  IconButton,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import { PROPERTY_TYPES } from '../types/property';
import {
  ref,
  uploadBytesResumable,
  getDownloadURL,
  listAll,
  getStorage,
} from "firebase/storage";
import OutlinedInput from '@mui/material/OutlinedInput';
import {storage} from '../firebaseconfig';
import { compressImage } from '../utils/compressImage';

export function BasicInformationSection({ formData, onChange, errors }) {
  
const uploadImageToFirebase = async (file) => {
    const fileToUpload =
      file.size > 300 * 1024
        ? await compressImage(file, { maxWidth: 1600, maxHeight: 1600, quality: 0.75 })
        : file;

    return new Promise((resolve, reject) => {
      const storageRef = ref(storage, `propertyImages/${Date.now()}_${fileToUpload.name}`);
      const uploadTask = uploadBytesResumable(storageRef, fileToUpload);

      uploadTask.on('state_changed',
        null,
        error => reject(error),
        async () => {
          const url = await getDownloadURL(uploadTask.snapshot.ref);
          resolve(url);
        }
      );
    });
  };

  const handleImageChange = async (e) => {
    const files = Array.from(e.target.files);
    if ((formData.images?.length || 0) + files.length > 30) {
      alert('Maximum 30 images allowed');
      return;
    }

    try {
      
      const urls = await Promise.all(files.map(uploadImageToFirebase));
      const newImages = urls.map(url => ({
        url,
        caption: '',
        isPrimary: false,
      }));

      onChange('images', [...(formData.images || []), ...newImages]);
    } catch (error) {
      console.error('Image upload failed:', error);
      alert('Failed to upload images. Please try again.');
    }
  };

   const handleRemoveImage = (index) => {
    const updatedImages = (formData.images || []).filter((_, i) => i !== index);
    onChange('images', updatedImages);
  };
  return (
    <Box sx={{ my: 3 }}>
      <Typography variant="h6" sx={{ mb: 3, color: 'text.primary', fontWeight: 700 }}>
        Basic Information
      </Typography>

      <Grid container spacing={2}>
        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Property Name *"
            id="propertyName"
            value={formData.propertyName || ''}
            onChange={(e) => onChange('propertyName', e.target.value)}
            placeholder="Enter property name"
            error={Boolean(errors?.propertyName)}
            helperText={errors?.propertyName}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <FormControl fullWidth error={Boolean(errors?.propertyType)} variant='outlined'>
            <InputLabel id="property-type-label" shrink variant='outlined'>Property Type *</InputLabel>
            <Select
              labelId="property-type-label"
              id="propertyType"
              value={formData.propertyType || ''}
              label="Property Type *"
              input={<OutlinedInput label="Property Type *" />}
              onChange={(e) => onChange('propertyType', e.target.value)}
              displayEmpty
            >
              <MenuItem value="">
                <em>Select property type</em>
              </MenuItem>
              {PROPERTY_TYPES.map((type) => (
                <MenuItem key={type.value} value={type.value}>
                  {type.label}
                </MenuItem>
              ))}
            </Select>
            {errors?.propertyType && (
              <FormHelperText>{errors.propertyType}</FormHelperText>
            )}
          </FormControl>
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Year Established"
            id="yearEstablished"
            type="number"
            value={formData.yearEstablished || ''}
            onChange={(e) => onChange('yearEstablished', e.target.value)}
            placeholder="e.g., 2010"
          />
        </Grid>

        <Grid item xs={12}>
          <TextField
            fullWidth
            multiline
            minRows={4}
            label="Property Description *"
            id="description"
            value={formData.description || ''}
            onChange={(e) => onChange('description', e.target.value)}
            placeholder="Describe your property, its unique features, ambiance, and what makes it special..."
            error={Boolean(errors?.description)}
           helperText={errors?.description || 
    (formData.description ? 
      `${formData.description.length}/2000` : 
      'Minimum 50 characters required'
    )
  }
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Total Area (sq ft)"
            id="totalArea"
            value={formData.totalArea || ''}
            onChange={(e) => onChange('totalArea', e.target.value)}
            placeholder="e.g., 5000"
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Number of Floors"
            id="numberOfFloors"
            type="number"
            value={formData.numberOfFloors || ''}
            onChange={(e) => onChange('numberOfFloors', e.target.value)}
            placeholder="e.g., 3"
          />
        </Grid>
             {/* Image upload section */}
        <Grid item xs={12}>
          <Typography variant="subtitle1" mb={1}>
            Upload Property Images (max 30)
          </Typography>
          <input
            type="file"
            accept="image/*"
            multiple
            onChange={handleImageChange}
          />
          <Box display="flex" flexWrap="wrap" gap={1} mt={1}>
            {(formData.images || []).map((img, idx) => (
              <Box key={idx} position="relative" sx={{ width: 100, height: 100, borderRadius: 1, overflow: 'hidden' }}>
                <img
                  src={img.url}
                  alt={`Property Image ${idx + 1}`}
                  style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                />
                <IconButton
                  size="small"
                  color="error"
                  onClick={() => handleRemoveImage(idx)}
                  sx={{ position: 'absolute', top: 0, right: 0 }}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Box>
            ))}
          </Box>
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Check-in Time *"
            id="checkInTime"
            type="time"
            value={formData.checkInTime || ''}
            onChange={(e) => onChange('checkInTime', e.target.value)}
            error={Boolean(errors?.checkInTime)}
            helperText={errors?.checkInTime}
            InputLabelProps={{ shrink: true }}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Check-out Time *"
            id="checkOutTime"
            type="time"
            value={formData.checkOutTime || ''}
            onChange={(e) => onChange('checkOutTime', e.target.value)}
            error={Boolean(errors?.checkOutTime)}
            helperText={errors?.checkOutTime}
            InputLabelProps={{ shrink: true }}
          />
        </Grid>
      </Grid>
    </Box>
  );
}
